import PropTypes from 'prop-types';
import { createContext, useMemo } from 'react';
import useReportController, {
  formatRangeDates,
  getDefaultPastMonthRange,
} from '../hooks/useReportController';
import { getProductReport } from '../services/reports';
import { DEFAULT_PRODUCT_CURRENCY_CODE, DOLARIZATION_SUPPORTED_CURRENCIES } from '../utils/currency';

export const ReportProductsContext = createContext({
  table: {},
  productsReport: [],
  countProducts: 0,
  fetchingProducts: false,
  dates: {},
  setDates: () => {},
  currency: DEFAULT_PRODUCT_CURRENCY_CODE,
  setCurrency: () => {},
  currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
  mutateExport: () => {},
  loadingExport: false,
});

const ReportProductsProvider = ({ children }) => {
  const {
    table,
    data,
    fetching: fetchingProducts,
    dates,
    setDates,
    currency,
    setCurrency,
    currencyOptions,
    mutateExport,
    loadingExport,
  } = useReportController({
    report: 'product',
    getDefaultDates: getDefaultPastMonthRange,
    formatDates: formatRangeDates,
    tableFilterOptions: { defaultCurrentPage: 1 },
    buildQueryKey: ({ page, dates: formattedDates, currency: apiCurrency }) => [
      'reports/product',
      page,
      formattedDates,
      apiCurrency,
    ],
    fetchReport: ({ page, dates: formattedDates, currency: apiCurrency }) =>
      getProductReport({ page, dates: formattedDates, currency: apiCurrency }),
    initialData: { results: [], count: 0 },
    buildExportParams: (extension) => ({ extension, type: 'producer' }),
    invalidateQueryKey: ['reports/product'],
  });

  const { results: productsReport = [], count: countProducts } = data;

  const value = useMemo(
    () => ({
      table,
      productsReport,
      countProducts,
      fetchingProducts,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      mutateExport,
      loadingExport,
    }),
    [
      table,
      productsReport,
      countProducts,
      fetchingProducts,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      mutateExport,
      loadingExport,
    ]
  );

  return <ReportProductsContext.Provider value={value}>{children}</ReportProductsContext.Provider>;
};

ReportProductsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ReportProductsProvider;
